// Solomon Portal v2.x — ReplyComposer
//
// In-place composer for replying to an existing Help Scout conversation
// OR creating a brand-new one. Replaces the Beacon-widget reply path.
//
// Spec: SPEC-reply-composer.md §2.1 + §2.2 + §2.4 + §7 + §8.3.
//
// UX contract (excerpt):
//   - When closed: not rendered. Parent flips a `composing` flag to mount us.
//   - When open: textarea + attachments + Send/Cancel. Send button is
//     disabled when (text empty AND no attachments) -- though new-
//     conversation mode requires non-empty text regardless.
//   - On submit: optimistic UI is the parent's job; we only call back
//     with the result.
//   - Drafts: textarea content is persisted to localStorage keyed by
//     `mode + conversationNumber` (so /support/4555 and /support/new
//     have independent drafts). Cleared on successful send or Cancel.
//   - Idempotency-Key: minted ONCE per Send press. Retries reuse the
//     same key so the server's cache de-dupes a network-flake duplicate.
//   - Attachment uploads: kicked off as the user picks each file. The
//     in-flight upload renders as a "uploading…" chip; on success it
//     becomes a regular attachment chip; on failure the chip shows a
//     typed error and a "Remove" affordance.

import * as React from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import {
  uploadAttachment,
  CLIENT_ALLOWED_MIME_TYPES,
  CLIENT_MAX_FILE_SIZE_BYTES,
  type StagedAttachment,
  type UploadAttachmentResult,
  type UploadRejectionReason,
} from '../../lib/upload-attachment-api';
import { newIdempotencyKey } from '../../lib/post-reply-api';

const MAX_TEXT_CHARS = 12000;
const TEXT_WARN_AT = 8000;
const MAX_ATTACHMENTS = 10;
const SUBJECT_MIN_CHARS = 3;
const SUBJECT_MAX_CHARS = 200;

const COMPOSER_HUMAN_MIME_LABEL: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'image/png': 'PNG image',
  'image/jpeg': 'JPEG image',
  'image/gif': 'GIF',
  'image/webp': 'WebP image',
  'application/msword': 'Word doc (.doc)',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word doc (.docx)',
  'application/vnd.ms-excel': 'Excel (.xls)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
  'text/plain': 'Plain text',
};

const CLIENT_ALLOWED_MIMES_LIST = Array.from(CLIENT_ALLOWED_MIME_TYPES).join(',');

export type ComposerMode = { kind: 'reply'; conversationNumber: number } | { kind: 'new' };

/** What the composer reports back to the parent on Send. The parent owns
 *  the actual API call (post-reply vs post-conversation, optimistic
 *  render, navigation). */
export interface ComposerSubmitPayload {
  /** The (possibly empty) text the user typed; trimmed by the parent. */
  text: string;
  /** New-conversation mode only; absent in reply mode. */
  subject?: string;
  /** Staged attachment ids in pick order. Empty if none. */
  attachmentIds: string[];
  /** Pre-minted idempotency key — caller MUST pass this to the API
   *  helper so retries dedupe server-side. */
  idempotencyKey: string;
}

export type ComposerErrorReason =
  | 'too_long'
  | 'empty'
  | 'subject_required'
  | 'subject_too_short'
  | 'subject_too_long'
  | 'too_many_attachments'
  | 'attachment_resolution_failed'
  | 'invalid_conversation_number'
  | 'invalid_attachment_id_shape'
  | 'invalid_json'
  | 'missing_or_invalid_idempotency_key'
  | 'forbidden'
  | 'unauthenticated'
  | 'upstream_unavailable'
  | 'config_error'
  | 'network_error'
  | 'unknown';

export interface ReplyComposerProps {
  mode: ComposerMode;
  /** Called when the user clicks Cancel. Parent should hide the
   *  composer and clear any in-page composer state. */
  onCancel: () => void;
  /** Called when the user clicks Send and inputs are valid. Parent
   *  drives the actual API call + optimistic render. The composer
   *  enters its in-flight state and will not allow another submit
   *  until `markFinished` (returned to parent via the `setLifecycle`
   *  ref-callback in `ReplyComposerHandle`) is called. */
  onSubmit: (payload: ComposerSubmitPayload) => Promise<void>;
  /** External error from the parent's API call. When set, the composer
   *  surfaces it inline above the Send button. */
  externalError: ComposerErrorReason | null;
  /** Distinguishes localStorage namespaces and disables sending while
   *  the parent is mid-submit. */
  inFlight: boolean;
}

/** Map a typed error reason to a human-readable inline message. The
 *  copy here is intentionally specific — generic "something went wrong"
 *  messages destroy users' trust in the surface. */
function errorMessage(reason: ComposerErrorReason | null): string | null {
  if (!reason) return null;
  switch (reason) {
    case 'empty':
      return 'Your reply is empty. Type a message or attach a file.';
    case 'too_long':
      return `Your reply is over the ${MAX_TEXT_CHARS}-character limit. Trim it down to send.`;
    case 'subject_required':
      return 'Add a subject line before sending.';
    case 'subject_too_short':
      return `The subject must be at least ${SUBJECT_MIN_CHARS} characters.`;
    case 'subject_too_long':
      return `The subject must be ${SUBJECT_MAX_CHARS} characters or fewer.`;
    case 'too_many_attachments':
      return `You can attach up to ${MAX_ATTACHMENTS} files per message.`;
    case 'attachment_resolution_failed':
      return 'One of your attachments couldn\u2019t be read. Remove it and try again.';
    case 'forbidden':
      return 'This conversation isn\u2019t available in the portal anymore. Your draft is preserved \u2014 copy it before navigating away.';
    case 'unauthenticated':
      return 'Your session expired. Sign in again to send.';
    case 'upstream_unavailable':
      return 'Our support backend is briefly unavailable. Try again in a moment.';
    case 'network_error':
      return 'Couldn\u2019t reach the server. Check your connection and try again.';
    case 'invalid_conversation_number':
    case 'invalid_attachment_id_shape':
    case 'invalid_json':
    case 'missing_or_invalid_idempotency_key':
    case 'config_error':
    case 'unknown':
      return 'Something went wrong sending your reply. Please try again.';
    default:
      return 'Something went wrong sending your reply. Please try again.';
  }
}

/** UI state for an attachment chip — covers in-flight + success +
 *  failure cases. The id is only present once the upload has succeeded
 *  (it's the server-minted uuid). */
type AttachmentChipState =
  | {
      kind: 'uploading';
      localId: string;
      filename: string;
      sizeBytes: number;
      controller: AbortController;
    }
  | {
      kind: 'staged';
      localId: string;
      attachment: StagedAttachment;
    }
  | {
      kind: 'failed';
      localId: string;
      filename: string;
      sizeBytes: number;
      reason: UploadRejectionReason | 'network_error' | 'unknown';
    };

function humanFileSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadFailReasonCopy(
  reason: UploadRejectionReason | 'network_error' | 'unknown',
  filename: string,
  mimeType: string,
): string {
  switch (reason) {
    case 'unsupported_mime':
      return `${filename}: ${COMPOSER_HUMAN_MIME_LABEL[mimeType] ?? (mimeType || 'this file type')} isn\u2019t supported.`;
    case 'too_large':
      return `${filename} is over ${humanFileSize(CLIENT_MAX_FILE_SIZE_BYTES)} \u2014 too large to send.`;
    case 'empty_file':
      return `${filename} is empty (0 bytes).`;
    case 'no_file':
    case 'invalid_form':
      return `${filename} couldn\u2019t be read \u2014 try removing and re-adding it.`;
    case 'network_error':
      return `Couldn\u2019t upload ${filename}. Check your connection and re-add it.`;
    case 'unknown':
    default:
      return `${filename} failed to upload.`;
  }
}

function draftStorageKey(mode: ComposerMode): string {
  return mode.kind === 'reply'
    ? `solomon-portal:composer-draft:reply:${mode.conversationNumber}`
    : 'solomon-portal:composer-draft:new';
}

interface PersistedDraft {
  text: string;
  subject?: string;
  /** ISO timestamp — used to render "Draft from <date>" if older than 24h. */
  savedAt: string;
}

function loadDraft(mode: ComposerMode): PersistedDraft | null {
  try {
    const raw = window.localStorage.getItem(draftStorageKey(mode));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDraft;
    if (typeof parsed.text !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveDraft(mode: ComposerMode, draft: PersistedDraft): void {
  try {
    if (draft.text.trim().length === 0 && (draft.subject ?? '').trim().length === 0) {
      window.localStorage.removeItem(draftStorageKey(mode));
      return;
    }
    window.localStorage.setItem(draftStorageKey(mode), JSON.stringify(draft));
  } catch {
    // localStorage can throw in private-browsing or quota-exceeded —
    // we accept silent failure here; the worst case is the user
    // re-types if they refresh.
  }
}

export function clearDraft(mode: ComposerMode): void {
  try {
    window.localStorage.removeItem(draftStorageKey(mode));
  } catch {
    /* noop */
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function ReplyComposer({
  mode,
  onCancel,
  onSubmit,
  externalError,
  inFlight,
}: ReplyComposerProps): React.ReactNode {
  // Initialize from any persisted draft on mount.
  const initialDraft = React.useMemo<PersistedDraft | null>(
    () => loadDraft(mode),
    // mode is stable for the lifetime of this composer mount; we
    // intentionally re-read only when remounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [text, setText] = React.useState<string>(initialDraft?.text ?? '');
  const [subject, setSubject] = React.useState<string>(
    mode.kind === 'new' ? (initialDraft?.subject ?? '') : '',
  );
  const [attachments, setAttachments] = React.useState<AttachmentChipState[]>([]);
  const [idempotencyKey, setIdempotencyKey] = React.useState<string>(() => newIdempotencyKey());

  // Persist on every text/subject change (debounced trivially via the
  // browser's natural input event cadence; saveDraft is cheap).
  React.useEffect(() => {
    const draft: PersistedDraft = {
      text,
      ...(mode.kind === 'new' ? { subject } : {}),
      savedAt: new Date().toISOString(),
    };
    saveDraft(mode, draft);
  }, [text, subject, mode]);

  // Lazy useState initializer is the React-blessed pattern for
  // capturing a one-shot impure value at mount without violating the
  // component-purity contract that flags Date.now() inside render.
  const [mountedAtMs] = React.useState<number>(() => Date.now());
  const draftIsStale = React.useMemo(() => {
    if (!initialDraft) return false;
    const t = Date.parse(initialDraft.savedAt);
    if (!Number.isFinite(t)) return false;
    return mountedAtMs - t > DAY_MS;
  }, [initialDraft, mountedAtMs]);

  const stagedIds = attachments
    .filter((a): a is Extract<AttachmentChipState, { kind: 'staged' }> => a.kind === 'staged')
    .map((a) => a.attachment.id);
  const hasInFlightUpload = attachments.some((a) => a.kind === 'uploading');

  const trimmedText = text.trim();
  const sendDisabled = (() => {
    if (inFlight) return true;
    if (hasInFlightUpload) return true;
    if (mode.kind === 'new') {
      if (subject.trim().length < SUBJECT_MIN_CHARS) return true;
      if (trimmedText.length === 0) return true;
    } else {
      if (trimmedText.length === 0 && stagedIds.length === 0) return true;
    }
    if (text.length > MAX_TEXT_CHARS) return true;
    if (mode.kind === 'new' && subject.length > SUBJECT_MAX_CHARS) return true;
    return false;
  })();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    e.target.value = ''; // allow re-selecting the same filename
    const remaining = MAX_ATTACHMENTS - attachments.length;
    const take = files.slice(0, Math.max(0, remaining));
    for (const file of take) {
      const localId = crypto.randomUUID();
      const controller = new AbortController();
      setAttachments((curr) => [
        ...curr,
        { kind: 'uploading', localId, filename: file.name, sizeBytes: file.size, controller },
      ]);
      void uploadAttachment({ file, signal: controller.signal }).then((result) => {
        setAttachments((curr) =>
          curr.map((a) => {
            if (a.localId !== localId) return a;
            return chipFromResult(localId, file, result);
          }),
        );
      });
    }
  };

  const removeAttachment = (localId: string): void => {
    setAttachments((curr) => {
      const target = curr.find((a) => a.localId === localId);
      if (target?.kind === 'uploading') target.controller.abort();
      return curr.filter((a) => a.localId !== localId);
    });
  };

  const cancelComposer = (): void => {
    // Cancel any pending uploads + clear local + persisted state.
    for (const a of attachments) if (a.kind === 'uploading') a.controller.abort();
    clearDraft(mode);
    onCancel();
  };

  const submit = async (): Promise<void> => {
    if (sendDisabled) return;
    await onSubmit({
      text: trimmedText,
      ...(mode.kind === 'new' ? { subject: subject.trim() } : {}),
      attachmentIds: stagedIds,
      idempotencyKey,
    });
    // Mint a fresh idempotency key for the NEXT send — old key stays
    // associated with the just-completed (or failed) submission so a
    // retry while inFlight=false replays cleanly.
    setIdempotencyKey(newIdempotencyKey());
  };

  const inlineError = errorMessage(externalError);
  const charsOver = text.length > MAX_TEXT_CHARS;
  const charsWarn = text.length >= TEXT_WARN_AT && !charsOver;

  return (
    <section
      aria-label={mode.kind === 'reply' ? 'Reply composer' : 'New conversation composer'}
      className="flex flex-col gap-3 rounded-md border border-border-light bg-thread-staff-bg p-4"
    >
      {draftIsStale ? (
        <p className="text-[12px] text-text-muted">
          Draft from {new Date(initialDraft!.savedAt).toLocaleString()} \u2014 review before
          sending.
        </p>
      ) : null}

      {mode.kind === 'new' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor="composer-subject" className="text-[13px]">
            Subject
          </Label>
          <Input
            id="composer-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={inFlight}
            maxLength={SUBJECT_MAX_CHARS}
            autoFocus
            placeholder="What\u2019s this about?"
            aria-invalid={
              externalError === 'subject_required' ||
              externalError === 'subject_too_short' ||
              externalError === 'subject_too_long'
                ? true
                : undefined
            }
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <Label htmlFor="composer-text" className="text-[13px]">
          {mode.kind === 'reply' ? 'Your reply' : 'Message'}
        </Label>
        <textarea
          id="composer-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={inFlight}
          rows={4}
          autoFocus={mode.kind === 'reply'}
          placeholder={
            mode.kind === 'reply'
              ? 'Type your reply\u2026'
              : 'Tell us what you need help with\u2026'
          }
          className={cn(
            'min-h-[7rem] resize-y rounded-sm border border-input bg-card p-3 text-[14px] leading-relaxed',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-1',
            charsOver ? 'border-destructive' : null,
          )}
          aria-describedby="composer-text-counter"
        />
        <p
          id="composer-text-counter"
          className={cn(
            'self-end text-[11px]',
            charsOver ? 'text-destructive' : charsWarn ? 'text-orange-deep' : 'text-text-muted',
            text.length < TEXT_WARN_AT ? 'invisible' : null,
          )}
        >
          {text.length} / {MAX_TEXT_CHARS}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="composer-attach" className="text-[13px]">
            Attachments{' '}
            {attachments.length > 0 ? `(${attachments.length}/${MAX_ATTACHMENTS})` : null}
          </Label>
          <input
            id="composer-attach"
            type="file"
            multiple
            disabled={inFlight || attachments.length >= MAX_ATTACHMENTS}
            onChange={handleFileChange}
            accept={CLIENT_ALLOWED_MIMES_LIST}
            className="text-[12px]"
          />
        </div>
        {attachments.length > 0 ? (
          <ul className="flex flex-col gap-1.5" aria-label="Attached files">
            {attachments.map((a) => (
              <AttachmentChip
                key={a.localId}
                state={a}
                onRemove={() => removeAttachment(a.localId)}
                disabled={inFlight}
              />
            ))}
          </ul>
        ) : null}
      </div>

      {inlineError ? (
        <p role="alert" className="text-[13px] text-destructive">
          {inlineError}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <p className="text-[12px] text-text-muted">
          {mode.kind === 'reply'
            ? 'Your reply will be sent to Solomon Advising on this conversation.'
            : 'This will start a new conversation with Solomon Advising.'}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={cancelComposer} disabled={inFlight}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={sendDisabled}>
            {inFlight ? 'Sending\u2026' : mode.kind === 'reply' ? 'Send reply' : 'Send'}
          </Button>
        </div>
      </div>
    </section>
  );
}

function chipFromResult(
  localId: string,
  file: File,
  result: UploadAttachmentResult,
): AttachmentChipState {
  switch (result.kind) {
    case 'ok':
      return { kind: 'staged', localId, attachment: result.attachment };
    case 'rejected':
      return {
        kind: 'failed',
        localId,
        filename: file.name,
        sizeBytes: file.size,
        reason: result.reason,
      };
    case 'aborted':
      return {
        kind: 'failed',
        localId,
        filename: file.name,
        sizeBytes: file.size,
        reason: 'unknown',
      };
    case 'network_error':
      return {
        kind: 'failed',
        localId,
        filename: file.name,
        sizeBytes: file.size,
        reason: 'network_error',
      };
    case 'unauthenticated':
    case 'forbidden':
    case 'config_error':
    default:
      return {
        kind: 'failed',
        localId,
        filename: file.name,
        sizeBytes: file.size,
        reason: 'unknown',
      };
  }
}

function AttachmentChip({
  state,
  onRemove,
  disabled,
}: {
  state: AttachmentChipState;
  onRemove: () => void;
  disabled: boolean;
}): React.ReactNode {
  switch (state.kind) {
    case 'uploading':
      return (
        <li className="flex items-center justify-between gap-3 rounded-sm border border-border-light bg-card px-3 py-2 text-[13px]">
          <span className="truncate">
            <span className="font-medium">{state.filename}</span>
            <span className="ml-2 text-text-muted">
              {humanFileSize(state.sizeBytes)} \u00b7 uploading\u2026
            </span>
          </span>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-[12px] text-text-muted hover:text-orange-deep"
          >
            Cancel
          </button>
        </li>
      );
    case 'staged':
      return (
        <li className="flex items-center justify-between gap-3 rounded-sm border border-border-light bg-card px-3 py-2 text-[13px]">
          <span className="truncate">
            <span className="font-medium">{state.attachment.filename}</span>
            <span className="ml-2 text-text-muted">{humanFileSize(state.attachment.size)}</span>
          </span>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-[12px] text-text-muted hover:text-orange-deep"
          >
            Remove
          </button>
        </li>
      );
    case 'failed':
      return (
        <li className="flex items-center justify-between gap-3 rounded-sm border border-destructive bg-card px-3 py-2 text-[13px]">
          <span className="truncate text-destructive">
            {uploadFailReasonCopy(state.reason, state.filename, '')}
          </span>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="text-[12px] text-text-muted hover:text-orange-deep"
          >
            Remove
          </button>
        </li>
      );
    default:
      return null;
  }
}
